/**
 * Capsule grid — signature 4×4 archive plate visual. Top-16 garments
 * arranged as Vogue archive cards with paper-stack shadow, hairline wear meters,
 * anchor-positioned detail labels. The aesthetic heart of Almari.
 */
import { store } from '../store.ts';
import { formatINR, CATEGORY_DISPLAY } from '../types.ts';
import type { ClothingItem } from '../types.ts';

export class AlmariCapsuleGrid extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.items, s.summary.insights.avg_cost_per_wear);
    });
  }
  disconnectedCallback(): void { this.#unsub?.(); }

  #render(items: ClothingItem[], avgCpw: number): void {
    if (!items.length) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }

    const top = [...items]
      .filter((it) => it.wear_count > 0)
      .sort((a, b) => (a.cost_per_wear || 1e9) - (b.cost_per_wear || 1e9))
      .slice(0, 16);

    if (top.length < 4) {
      this.#shadow.innerHTML = '<style>:host{display:none;}</style>';
      return;
    }

    const plates = top.map((it, i) => {
      const meter = Math.min(100, Math.round((it.wear_count / Math.max(1, top[0]!.wear_count)) * 100));
      const verdict = it.cost_per_wear <= avgCpw ? 'roi' : 'drift';
      const anchorName = `--plate-${i}`;
      const hangDelay = `${i * 70}ms`;
      const hangRot = (i % 2 === 0 ? -0.4 : 0.4) + (i / 24);
      return /* html */ `
<article class="plate" data-verdict="${verdict}" style="--plate-anchor:${anchorName}; --wear-fill:${meter}%; --hang-delay:${hangDelay}; --hang-rot:${hangRot}deg; view-transition-name:${anchorName.slice(2)};">
  <div class="plate-no">PLATE &middot; ${String(i + 1).padStart(2, '0')}</div>
  <div class="plate-name">${escape(it.name)}</div>
  <div class="plate-brand">${escape(it.brand)}</div>
  <div class="plate-cat">${CATEGORY_DISPLAY[it.category] ?? it.category}</div>
  <div class="plate-meter" aria-label="wear meter"><span></span></div>
  <div class="plate-foot">
    <span class="plate-wears">${it.wear_count} wears</span>
    <span class="plate-cpw">${formatINR(it.cost_per_wear)}/w</span>
  </div>
</article>`;
    }).join('');

    this.#shadow.innerHTML = /* html */ `
<style>
:host { display: block; container-type: inline-size; }

@property --plate-lift {
  syntax: '<length>';
  inherits: false;
  initial-value: 0px;
}

.head {
  display: grid;
  gap: 6px;
  padding-left: 4px;
  margin-bottom: 14px;
}
.head-no {
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.45em;
  text-transform: uppercase;
  color: var(--brass-dim);
}
.head-name {
  font-family: var(--display);
  font-style: italic;
  font-size: 22px;
  font-weight: 600;
  color: var(--ink);
  letter-spacing: -0.005em;
  text-wrap: balance;
}
.head-sub {
  font-family: var(--display);
  font-style: italic;
  font-size: 14px;
  color: var(--ink-dim);
  text-wrap: pretty;
}

.grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px;
}
@container (min-width: 460px) {
  .grid { grid-template-columns: repeat(4, 1fr); gap: 12px; }
}

.plate {
  position: relative;
  background: var(--paper-card);
  border: 1px solid var(--rule);
  padding: 14px 14px 16px;
  display: grid;
  gap: 5px;
  isolation: isolate;
  transform: translateY(var(--plate-lift, 0px)) rotate(0deg);
  transition: --plate-lift 320ms cubic-bezier(0.34, 1.56, 0.64, 1), background 220ms ease, transform 320ms cubic-bezier(0.34, 1.56, 0.64, 1);
  animation:
    plate-hang 980ms cubic-bezier(0.22, 1, 0.36, 1) var(--hang-delay, 0ms) both,
    plate-settle 1600ms cubic-bezier(0.45, 0, 0.55, 1) calc(var(--hang-delay, 0ms) + 700ms) both;
  transform-origin: 50% 0%;
}
.plate:hover,
.plate:focus-within {
  transform: translateY(-4px) rotate(0deg) !important;
  animation-play-state: paused;
}

.plate::before {
  content: '';
  position: absolute;
  inset: 4px -3px -4px 4px;
  background: var(--paper-up);
  border: 1px solid var(--rule);
  z-index: -2;
  transform: rotate(0.3deg);
}
.plate::after {
  content: '';
  position: absolute;
  inset: 7px -6px -7px 7px;
  background: color-mix(in oklch, var(--paper-up) 88%, var(--brass));
  border: 1px solid var(--rule);
  z-index: -3;
  transform: rotate(-0.4deg);
  opacity: 0.7;
}

.plate:hover,
.plate:focus-within {
  --plate-lift: -4px;
  background: color-mix(in oklch, var(--brass) 8%, var(--paper-card));
  box-shadow: 0 4px 18px -10px color-mix(in oklch, var(--brass-deep) 60%, transparent);
}

.plate[data-verdict="roi"] {
  border-color: color-mix(in oklch, var(--leaf) 38%, var(--rule));
}
.plate[data-verdict="drift"] {
  border-color: color-mix(in oklch, var(--rose) 22%, var(--rule));
}

.plate-no {
  font-family: var(--mono);
  font-size: 8px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--brass-dim);
}
.plate-name {
  font-family: var(--display);
  font-size: 17px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.01em;
  line-height: 1.1;
  text-wrap: balance;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  min-height: 2.2em;
}
.plate-brand {
  font-family: var(--display);
  font-style: italic;
  font-size: 13px;
  color: var(--brass-dim);
  font-weight: 500;
}
.plate-cat {
  font-family: var(--mono);
  font-size: 8px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
.plate-meter {
  margin-top: 4px;
  height: 1px;
  background: color-mix(in oklch, var(--rule) 60%, transparent);
  position: relative;
  overflow: hidden;
}
.plate-meter span {
  display: block;
  position: absolute;
  inset: 0 auto 0 0;
  width: var(--wear-fill);
  background: var(--ink);
  animation: meter-grow 900ms cubic-bezier(0.32, 0.72, 0, 1) 240ms both;
  transform-origin: left;
}
.plate-foot {
  display: flex;
  justify-content: space-between;
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--ink-dim);
  font-feature-settings: 'tnum' 1, 'ss01' 1;
  margin-top: 2px;
}
.plate-cpw { color: var(--ink); font-weight: 700; }
.plate[data-verdict="roi"] .plate-cpw { color: var(--leaf); }
.plate[data-verdict="drift"] .plate-cpw { color: var(--rose); }

.tail {
  margin-top: 18px;
  padding: 0 4px;
  display: grid;
  gap: 8px;
}
.tail-line {
  display: flex;
  justify-content: space-between;
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
.tail-line strong {
  font-family: var(--display);
  font-style: italic;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: 0;
  text-transform: none;
  font-size: 14px;
}

@keyframes plate-hang {
  0%   { opacity: 0; transform: translateY(-26px) rotate(calc(var(--hang-rot, 0deg) * 3.4)) scale(0.96); }
  55%  { opacity: 1; transform: translateY(2px) rotate(calc(var(--hang-rot, 0deg) * -1.4)) scale(1.005); }
  100% { opacity: 1; transform: translateY(0) rotate(var(--hang-rot, 0deg)) scale(1); }
}
@keyframes plate-settle {
  0%, 100% { transform: translateY(0) rotate(var(--hang-rot, 0deg)); }
  35%      { transform: translateY(0) rotate(calc(var(--hang-rot, 0deg) * 0.6)); }
  70%      { transform: translateY(0) rotate(calc(var(--hang-rot, 0deg) * 1.2)); }
}
@keyframes meter-grow {
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
}

@media (prefers-reduced-motion: reduce) {
  .plate { animation: none; transform: none; }
  .plate-meter span { animation: none; }
}
</style>
<header class="head">
  <div class="head-no">Capsule · top 16 by cost-per-wear</div>
  <div class="head-name">The archive plates</div>
  <div class="head-sub">Sorted by ROI · paper-stack depth from contact-sheet press</div>
</header>
<div class="grid">${plates}</div>
<div class="tail">
  <div class="tail-line"><span>Reading order</span><strong>ROI first · drift last</strong></div>
  <div class="tail-line"><span>Edition</span><strong>Vol. I · Plates 01-16</strong></div>
</div>
`;
  }
}

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
}

customElements.define('almari-capsule-grid', AlmariCapsuleGrid);
